import React, { useEffect, useMemo, useState } from 'react';
import { Users as UsersIcon, RefreshCw } from 'lucide-react';
import './UserPage.css';

import SlideOver from '../../components/ui/SlideOver/SlideOver';
import UsersTable from '../../features/users/UsersTable/UsersTable';
import UserPanel from '../../features/users/UserPanel/UserPanel';
import ResetPasswordDialog from '../../features/users/ResetPasswordDialog/ResetPasswordDialog';
import DeleteUserDialog from '../../features/users/DeleteUserDialog/DeleteUserDialog';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import ListToolbar from '../../components/ui/ListToolbar/ListToolbar';

import listarUsuarios from '../../API/usuarios/Usuarios';
import getCondominios from '../../API/condos/Condos';

export default function UsersPage() {
  const [q, setQ] = useState('');
  const [openCreate, setOpenCreate] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [condominios, setCondominios] = useState([]);

  const loadUsers = async () => {
    let alive = true;
    setLoading(true);
    setErr('');
    try {
      const res = await listarUsuarios(); // service deve injetar token
      if (!res?.ok) {
        throw new Error(res?.error || `request_failed_${res?.status ?? ''}`);
      }

      // trata ambos formatos possíveis de payload
      const raw = Array.isArray(res?.data) ? res.data : (res?.data?.users ?? []);
      const list = raw.map(normalizeUser);

      if (alive) setUsers(list.sort(sortByCreatedDesc));
    } catch (e) {
      if (alive) setErr('Falha ao carregar usuários.');
      console.error(e);
    } finally {
      if (alive) setLoading(false);
    }
    return () => { alive = false; };
  };

  const loadCondominios = async () => {
    try {
      const res = await getCondominios();
      const condominios = Array.isArray(res?.data?.condominios) ? res.data.condominios : [];

      const list = [
        { id: 'all', name: 'all', domain: '' },
        ...condominios.filter(c => (c?.name || '').toLowerCase() !== 'all')
      ];

      setCondominios(list);
    } catch (e) {
      console.error('Falha ao carregar condomínios', e);
      setCondominios([{ id: 'all', name: 'all', domain: '' }]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadCondominios();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u =>
      [u.name, u.user, u.email, u.condominio]
        .some(v => String(v || '').toLowerCase().includes(needle))
    );
  }, [q, users]);

  return (
    <div className="users-page">
      <PageHeader
        title="Usuários"
        subtitle="Gerencie acessos, permissões e condomínios vinculados."
        icon={<UsersIcon strokeWidth={1.8} />}
        iconVariant="green"
        rightContent={
          <>
            <button
              type="button"
              className="users-refresh-btn"
              onClick={loadUsers}
              disabled={loading}
            >
              <RefreshCw
                className={loading ? 'users-refresh-icon spinning' : 'users-refresh-icon'}
                strokeWidth={1.8}
              />
              <span>{loading ? 'Atualizando...' : 'Atualizar lista'}</span>
            </button>

            <ListToolbar
              value={q}
              onChange={setQ}
              onNew={() => setOpenCreate(true)}
              searchPlaceholder="Buscar usuário..."
              newLabel="Novo usuário"
            />
          </>
        }
      />

      <main className="users-content">
        <section className="users-card">
          <UsersTable
            users={filtered}
            loading={loading}
            error={err}
            onEdit={(u) => { setTargetUser(u); setOpenCreate(true); }}
            onResetPwd={(u) => { setTargetUser(u); setOpenReset(true); }}
            onDelete={(u) => { setTargetUser(u); setOpenDelete(true); }}
          />
        </section>
      </main>

      <SlideOver
        open={openCreate}
        onClose={() => { setOpenCreate(false); setTargetUser(null); }}
        title={targetUser ? `Editar: ${targetUser.name}` : 'Novo usuário'}
      >
        <UserPanel
          mode={targetUser ? 'edit' : 'create'}
          user={targetUser}
          condominios={condominios}
          onClose={() => { setOpenCreate(false); setTargetUser(null); }}
          onSaved={loadUsers}
        />
      </SlideOver>

      <ResetPasswordDialog
        open={openReset}
        user={targetUser}
        onClose={() => { setOpenReset(false); setTargetUser(null); }}
        onReset={loadUsers}
      />

      <DeleteUserDialog
        open={openDelete}
        user={targetUser}
        onClose={() => { setOpenDelete(false); setTargetUser(null); }}
        onDeleted={loadUsers}
      />
    </div>
  );
}

// Helpers

function sortByCreatedDesc(a, b) {
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

function normalizeUser(u) {
  return {
    id: u.id,
    user: u.user,
    name: u.name,
    email: u.email,
    role: u.role,
    condominio: u.condominio,
    created_at: u.created_at,
    report: normalizeReport(u.report || {}),
    table: u.table,
  };
}

function normalizeReport(report) {
  const keys = ['cmd', 'sindico', 'construtora', 'administrador', 'fornecedor'];
  const out = {};
  for (const k of keys) {
    out[k] = Object.entries(report).some(([rk, rv]) => rk.toLowerCase() === k && !!rv);
  }
  return out;
}